/**
 * A click-to-sort table header cell for server-sorted admin lists.
 *
 * The button carries the accessible name and the `<th>` carries
 * `aria-sort`, which is where assistive tech reads the current
 * direction from — not the arrow glyph, which is `aria-hidden`.
 *
 * Generic over the sort key so each list can keep a closed vocabulary
 * (for example `'name' | 'createdAt'` on the client list) without widening
 * it to `string`.
 */

import {
  Icon,
  Tooltip,
} from '@speaking-coach/ui'
import { ColumnFilterDot } from './column-filter-indicator'

export type SortDir = 'asc' | 'desc'

interface SortableThProps<K extends string> {
  label: string
  columnKey: K
  activeKey: K
  dir: SortDir
  onSort: (key: K) => void
  onMultiSort?: (key: K) => void
  priority?: number
  /** Native tooltip — use it to explain what the column actually means. */
  title?: string
  filterDescription?: string
}

export function SortableTh<K extends string>({
  label,
  columnKey,
  activeKey,
  dir,
  onSort,
  onMultiSort,
  priority,
  title,
  filterDescription,
}: SortableThProps<K>): JSX.Element {
  const active = priority !== undefined || columnKey === activeKey
  const description = [filterDescription, priority !== undefined ? `Sort priority ${priority}, ${dir === 'asc' ? 'ascending' : 'descending'}.` : undefined].filter(Boolean).join(' ') || undefined
  // Keep the full cell clickable, with the sort arrow last and the filter
  // marker raised beside the label. One focus target exposes both semantics.
  const button = (
    <button
      type="button"
      onClick={event => { if (event.shiftKey && onMultiSort) onMultiSort(columnKey); else onSort(columnKey) }}
      title={filterDescription ? undefined : title}
      aria-description={description}
      className="group flex w-full cursor-pointer items-center justify-between gap-2 p-3 text-left font-medium hover:bg-surface-app focus-visible:focus-ring-inset"
    >
      <span className="whitespace-nowrap">
        {label}{filterDescription && <ColumnFilterDot />}
      </span>
      <span
        aria-hidden="true"
        className={`inline-flex h-6 shrink-0 items-center justify-end gap-1.5 ${active ? 'text-text-primary' : 'text-text-secondary/50 group-hover:text-text-secondary'}`}
      >
        {priority !== undefined ? <span className="inline-flex size-5 items-center justify-center rounded-full bg-surface-app text-table-secondary font-medium leading-none tabular-nums">{priority}</span> : null}
        <span className="inline-flex w-3 items-center justify-center">{active ? <Icon name={dir === 'asc' ? 'sortAscending' : 'sortDescending'} size={12} className="scale-x-75" /> : '↕'}</span>
      </span>
    </button>
  )
  return (
    <th
      className="font-medium"
      aria-description={description}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      {filterDescription
        ? <Tooltip align="start" alignOffset={12} arrow="start" label={`${label} filter: ${filterDescription}`}>{button}</Tooltip>
        : button}
    </th>
  )
}

/*
 * `byNullableString` — an in-browser sort over the fetched window — lived
 * here until the client list moved its sorting into SQL (PROV-SCALE).
 * Sortable lists send `sortField`/`sortDirection` and render what comes
 * back, so a client-side sorter has nothing left to sort: it can
 * only ever reorder one page, which looks identical to sorting the table
 * and is not. If a new list needs sorting, give its endpoint the
 * parameters rather than reviving this.
 */
